import { EditOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Radio, Row } from 'antd';
import { EditUserHeader } from 'components/EditUser/EditUserHeader';
import { useErrorHandler } from 'components/ErrorHandler/ErrorHandlerContext';
import { StyledFormItem } from 'components/Forms/FormItemComponent';
import { getPreviewPostAddress } from 'lib/Antd/Preview';
import { observer } from 'mobx-react';
import { useRootStore } from 'models/rootModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getUser, updateUser } from 'utils/api';
import { formatMobilePhoneNumber } from 'utils/caseHelper';
import { CaseStatus } from 'utils/enumerations';
import { AccountType } from 'utils/enumerations/accountType';
import {
  organisationNumberValidation,
  socialSecurityNumberValidation,
  validateMessages
} from 'utils/forms/validateMessages';

const LabelContainer = styled.div`
  font-weight: bold;
`;

const FormContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 10px;
`;
const ButtonContainer = styled.div`
  padding-top: 15px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  margin-top: 10px;
`;

const formatSocialAndOrgNumber = value => {
  return value && value.length === 10 ? `${value.substr(0, 6)}-${value.substr(6, 4)}` : value;
};

export const EditUserForm = observer(({ logoutApi, visibleFnc }) => {
  const { t } = useTranslation();
  const [editable, setEditable] = useState(false);
  const rootStore = useRootStore();
  const [form] = Form.useForm();
  const { addError } = useErrorHandler();
  const { user } = rootStore;
  const [userType, setUserType] = useState(user.accountType);

  const numberRequiredValidator = async (value, minLength) => {
    if (value && isNaN(Number(value))) {
      return Promise.reject(validateMessages(t).types.number);
    } else if (!value) {
      return Promise.reject(validateMessages(t).required);
    } else if (value.length < minLength) {
      return Promise.reject(t('validateMessage:requiredMinLength', { length: minLength }));
    } else {
      return Promise.resolve();
    }
  };

  const onEdit = () => {
    setEditable(!editable);
  };

  const onSave = async () => {
    await form.validateFields();
    if (userType === AccountType.Personal) {
      if (user.socialSecurityNumber) {
        user.setValues({
          organisationName: '',
          organisationNumber: ''
        });
      }
    } else {
      if (user.organisationName && user.organisationNumber) {
        user.setValues({
          socialSecurityNumber: ''
        });
      }
    }
    setEditable(!editable);
    await updateUser(user);
    updateOpenApplication();
  };

  const updateOpenApplication = () => {
    let openApplication = rootStore.activeApplication;
    if (openApplication) {
      if (openApplication.status <= CaseStatus.Created) {
        openApplication.applicant.setValues({
          applicantName: user.name,
          organisationName: user.organisationName,
          phoneNumber: user.phoneNumber,
          mobilePhoneNumber: user.mobilePhoneNumber,
          email: user.email,
          socialSecurityNumber: user.socialSecurityNumber,
          organisationNumber: user.organisationNumber
        });

        openApplication.billingInformation.setValues({
          billingAddress: user.address,
          billingPostalCode: user.zipCode,
          billingPostalCity: user.city
        });
      }
    } else {
      //no open applications, dashboard need to fetch applications from backend again
      window.location.reload();
    }
  };

  const onCancelEdit = async () => {
    try {
      let savedUser = await getUser();
      rootStore.setUser(savedUser);
      form.setFieldsValue(savedUser);
    } catch (error) {
      addError(error, t('general:errorUser'));
    }
    setEditable(false);
  };

  const onValuesChange = changedValues => {
    user.setValues(changedValues);
  };

  const onChangeAccountType = e => {
    setUserType(e.target.value);
  };
  const orgNrValidation = organisationNumberValidation(t);
  const socNrValidation = socialSecurityNumberValidation(t);

  return (
    <>
      <EditUserHeader logoutApi={logoutApi} visibleFnc={visibleFnc}></EditUserHeader>

      {editable ? (
        <>
          <StyledRadioGroup disabled={!editable} onChange={onChangeAccountType} value={userType}>
            <Radio data-testid="accountTypePersonal" value={AccountType.Personal}>
              {t('identity:person')}
            </Radio>
            <Radio data-testid="accountTypeOrganisation" value={AccountType.Organisation}>
              {t('identity:organization')}
            </Radio>
          </StyledRadioGroup>
          <Radio.Group disabled={!editable} onChange={onChangeAccountType} value={userType}></Radio.Group>

          <FormContainer>
            <Form name="UserForm" onValuesChange={onValuesChange} initialValues={user} form={form}>
              <Row>
                <Col span={24}>
                  <LabelContainer>{t('applicationFields:name')}</LabelContainer>
                  <StyledFormItem name="name" rules={[{ required: true, message: validateMessages(t).required }]}>
                    <Input data-testid="userNameInput" maxLength={50} placeholder={user.name} />
                  </StyledFormItem>
                </Col>
              </Row>
              {userType === AccountType.Organisation && (
                <Row>
                  <Col span={24}>
                    <LabelContainer>{t('applicationFields:organisationName')}</LabelContainer>
                    <StyledFormItem
                      name="organisationName"
                      rules={[{ required: true, message: validateMessages(t).required }]}
                    >
                      <Input data-testid="orgNameInput" maxLength={50} placeholder={user.organisationName} />
                    </StyledFormItem>
                  </Col>
                </Row>
              )}
              <Row>
                <Col span={12}>
                  {userType === AccountType.Personal ? (
                    <>
                      <LabelContainer>{t('applicationFields:socialSecurityNumber')}</LabelContainer>
                      <StyledFormItem
                        name="socialSecurityNumber"
                        rules={[
                          { required: true, message: validateMessages(t).required },
                          { validator: socNrValidation }
                        ]}
                      >
                        <Input
                          data-testid="socSecNrInput"
                          maxLength={10}
                          placeholder={user.socialSecurityNumber ? user.socialSecurityNumber : 'YYMMDDXXXX'}
                        />
                      </StyledFormItem>
                    </>
                  ) : (
                    <>
                      <LabelContainer>{t('applicationFields:organisationNumber')}</LabelContainer>
                      <StyledFormItem
                        name="organisationNumber"
                        rules={[
                          { required: true, message: validateMessages(t).required },
                          { validator: orgNrValidation }
                        ]}
                      >
                        <Input
                          data-testid="organisationNrInput"
                          maxLength={10}
                          placeholder={user.organisationNumber ? user.organisationNumber : 'XXXXXXXXXX'}
                        />
                      </StyledFormItem>
                    </>
                  )}
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <LabelContainer>{t('applicationFields:email')}</LabelContainer>
                  <div data-testid="view-email">{user.email}</div>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <>
                    <LabelContainer>{t('applicationFields:address')}</LabelContainer>
                    <StyledFormItem name="address" rules={[{ required: true, message: validateMessages(t).required }]}>
                      <Input data-testid="addressInput" maxLength={50} placeholder={user.address} />
                    </StyledFormItem>
                  </>
                </Col>
              </Row>
              <Row>
                <Col span={7}>
                  <LabelContainer>{t('applicationFields:postalCode')}</LabelContainer>
                  <StyledFormItem
                    name="zipCode"
                    rules={[
                      {
                        validator: (_, value) => numberRequiredValidator(value, 5)
                      }
                    ]}
                  >
                    <Input data-testid="zipCodeInput" maxLength={5} placeholder={user.zipCode} />
                  </StyledFormItem>
                </Col>
                <Col span={17}>
                  <LabelContainer>{t('applicationFields:city')}</LabelContainer>
                  <StyledFormItem name="city" rules={[{ required: true, message: validateMessages(t).required }]}>
                    <Input data-testid="cityInput" maxLength={50} placeholder={user.city} />
                  </StyledFormItem>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <LabelContainer>{t('applicationFields:mobilePhoneNumber')}</LabelContainer>
                  <StyledFormItem
                    name="mobilePhoneNumber"
                    rules={[
                      {
                        validator: (_, value) => numberRequiredValidator(value, 10)
                      }
                    ]}
                  >
                    <Input data-testid="mobilePhoneNrInput" maxLength={10} placeholder={user.mobilePhoneNumber} />
                  </StyledFormItem>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <LabelContainer>{t('applicationFields:phoneNumber')}</LabelContainer>
                  <StyledFormItem
                    name="phoneNumber"
                    rules={[
                      {
                        pattern: new RegExp(/^0/), //starts with zero
                        message: t('validateMessage:allowedPhoneNrStart')
                      },
                      {
                        validator: (_, value) => {
                          //if value exist, only numbers
                          if (!value || (value && /^[0-9]+$/.test(value))) {
                            //correct length
                            if (value.length === 0 || (value.length >= 8 && value.length <= 10)) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(t('validateMessage:allowedPhoneNrLength'));
                            }
                          } else {
                            return Promise.reject(validateMessages(t).types.number);
                          }
                        }
                      }
                    ]}
                  >
                    <Input data-testid="phoneNrInput" maxLength={10} placeholder={user.phoneNumber} />
                  </StyledFormItem>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <ButtonContainer>
                    <Button type="primary" onClick={onSave} data-testid="saveUserInformationButton">
                      {t('general:save')}
                    </Button>
                  </ButtonContainer>
                </Col>
                <Col span={12}>
                  <ButtonContainer>
                    <Button onClick={onCancelEdit} data-testid="saveUserInformationButton">
                      {t('general:cancel')}
                    </Button>
                  </ButtonContainer>
                </Col>
              </Row>
            </Form>
          </FormContainer>
        </>
      ) : (
        <FormContainer>
          <Row>
            <Col span={24}>
              <LabelContainer>{t('applicationFields:name')}</LabelContainer>
              <div data-testid="popover-name">{user.name}</div>
              {user.accountType === AccountType.Organisation && (
                <div data-testid="popover-orgName">{user.organisationName}</div>
              )}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              {user.accountType === AccountType.Personal ? (
                <>
                  <LabelContainer>{t('applicationFields:socialSecurityNumber')}</LabelContainer>
                  <div data-testid="popover-socSecNr">{formatSocialAndOrgNumber(user.socialSecurityNumber)}</div>
                </>
              ) : (
                <>
                  <LabelContainer>{t('applicationFields:organisationNumber')}</LabelContainer>
                  <div data-testid="popover-orgNr">{formatSocialAndOrgNumber(user.organisationNumber)}</div>
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <LabelContainer>{t('applicationFields:email')}</LabelContainer>
              <div data-testid="popover-email">{user.email}</div>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <LabelContainer>{t('applicationFields:address')}</LabelContainer>
              <div data-testid="popover-address">{user.address}</div>
              <div data-testid="popover-zipCity">{getPreviewPostAddress(user.zipCode, user.city)}</div>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <LabelContainer>{t('applicationFields:mobilePhoneNumber')}</LabelContainer>
              <div data-testid="popover-mobileNr">{formatMobilePhoneNumber(user.mobilePhoneNumber)}</div>
            </Col>
          </Row>
          {user.phoneNumber && user.phoneNumber.length > 0 ? (
            <Row>
              <Col span={24}>
                <LabelContainer>{t('applicationFields:phoneNumber')}</LabelContainer>
                <div data-testid="popover-phoneNr">{user.phoneNumber}</div>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col span={24}>
              {!editable && (
                <ButtonContainer>
                  <Button data-testid="editUserInfoButton" type="link" onClick={onEdit} icon={<EditOutlined />}>
                    {t('identity:editUserInformation')}
                  </Button>
                </ButtonContainer>
              )}
            </Col>
          </Row>
        </FormContainer>
      )}
    </>
  );
});
